import "react-day-picker/dist/style.css";

import { Temporal, useFormatDateTime, useMessage, useNow } from "@hermes/intl";
import { type ReactNode, useEffect } from "react";
import { type Placement } from "react-aria";
import type { Matcher } from "react-day-picker";

import type { TriggerChildrenProps } from "../button/trigger-button";
import { cssFns } from "../css";
import { type Field, useFormField } from "../fields/common/form";
import {
  RadioGroup,
  type RadioGroupOption,
  type RadioGroupState,
  useRadioGroupState,
} from "../fields/radio-group";
import { Select } from "../fields/selectable/select";
import type { OverlayContentOptions } from "../overlays/content";
import { DynamicPopover } from "../overlays/dynamic-popover";
import { useScreenDetector } from "../screen-type";
import { colors } from "../theme";
import type { SizeUnit } from "../utils";
import { Calendar } from "./day-picker-wrapper";

export const DatePicker = ({
  value,
  onChange,
  children,
  disabled,
  placement,
  offset,
  minDate,
  maxDate,
  mobile,
}: {
  value?: Temporal.PlainDate;
  onChange?: (value?: Temporal.PlainDate) => void;
  disabled?: Matcher | Matcher[] | undefined;
  placement?: Placement;
  offset?: number;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  mobile?: { title?: string; height?: SizeUnit | undefined };
} & TriggerChildrenProps) => {
  const now = Temporal.Now.plainDateISO();
  const tomorrow = now.add({ days: 1 });

  const presetField = useFormField(
    useRadioGroupState({
      value:
        value?.toString() === now.toString()
          ? now.toString()
          : value?.toString() === tomorrow.toString()
            ? tomorrow.toString()
            : "",
    }),
  );

  useEffect(() => {
    presetField.onChange({
      value:
        value?.toString() === now.toString()
          ? now.toString()
          : value?.toString() === tomorrow.toString()
            ? tomorrow.toString()
            : "",
    });
  }, [value?.toString()]);

  return (
    <CalendarPopover
      mobile={mobile}
      calendar={(options) => (
        <Calendar
          minDate={minDate}
          maxDate={maxDate}
          value={value}
          onChange={(value) => {
            onChange?.(value);
            options.close();
          }}
          disabled={disabled}
        />
      )}
      presets={(options) => (
        <Presets
          field={presetField}
          onSelectionChange={(value) => {
            if (value && value.value !== "") {
              onChange?.(Temporal.PlainDate.from(value.value));
              options.close();
            }
          }}
        />
      )}
      placement={placement}
      offset={offset}
      children={children}
    />
  );
};

export const DateTimePicker = ({
  value = Temporal.Now.plainDateTimeISO(),
  onChange,
  children,
  disabled,
  placement,
  offset,
  minDate,
  maxDate,
}: {
  value?: Temporal.PlainDateTime;
  onChange?: (value?: Temporal.PlainDateTime) => void;
  disabled?: boolean;
  placement?: Placement;
  offset?: number;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
} & TriggerChildrenProps) => {
  const date = value.toPlainDate();
  const time = value.toPlainTime();

  const renderTime = (time: number) =>
    (time < 10 ? "0" + time : time).toString();

  return (
    <CalendarPopover
      calendar={() => (
        <Calendar
          value={date}
          onChange={(date) => {
            if (!date) {
              onChange?.(undefined);
              return;
            }

            onChange?.(
              date.toPlainDateTime(value.toPlainTime().with({ second: 0 })),
            );
          }}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
        />
      )}
      time={
        <div css={[cssFns.padding(4), cssFns.center]}>
          <div
            css={[
              {
                display: "grid",
                gridTemplateColumns: "80px max-content 80px",
                alignItems: "center",
                gap: 8,
              },
            ]}
          >
            <Select
              field={{
                value: renderTime(time.hour),
                onChange(state) {
                  if ("value" in state) {
                    onChange?.(
                      date.toPlainDateTime(
                        typeof state.value !== "undefined" &&
                          state.value !== null
                          ? value
                              .toPlainTime()
                              .with({ hour: parseInt(state.value) })
                          : value.toPlainTime(),
                      ),
                    );
                  }
                },
              }}
              options={Array.from({ length: 24 }).map((_, i) => {
                const time = renderTime(i);

                return {
                  label: time,
                  value: time,
                };
              })}
              showArrowDown={false}
            />
            <span css={{ fontSize: 20 }}>:</span>
            <Select
              field={{
                value: renderTime(time.minute),
                onChange(state) {
                  if ("value" in state) {
                    onChange?.(
                      date.toPlainDateTime(
                        typeof state.value !== "undefined" &&
                          state.value !== null
                          ? value
                              .toPlainTime()
                              .with({ minute: parseInt(state.value) })
                          : value.toPlainTime(),
                      ),
                    );
                  }
                },
              }}
              options={Array.from({ length: 60 }).map((_, i) => {
                const time = renderTime(i);

                return {
                  label: time,
                  value: time,
                };
              })}
              showArrowDown={false}
            />
          </div>
        </div>
      }
      placement={placement}
      offset={offset}
      children={children}
    />
  );
};

function CalendarPopover({
  children,
  placement,
  offset,
  calendar,
  time,
  mobile = { height: 430 },
  presets,
}: {
  placement?: Placement;
  offset?: number;
  calendar?: (options: OverlayContentOptions) => ReactNode;
  time?: ReactNode;
  mobile?: {
    title?: string;
    height?: SizeUnit | undefined;
  };
  presets?: (options: OverlayContentOptions) => ReactNode;
} & TriggerChildrenProps) {
  return (
    <DynamicPopover
      mobile={{
        ...mobile,
        closeButton: true,
      }}
      desktop={{
        width: "auto",
        placement,
        offset,
      }}
      content={(options) => (
        <div
          css={[
            cssFns.boxShadow({
              offsetX: "0px",
              offsetY: "0px",
              blurRadius: "20px",
              color: "#f1f1f1",
            }),
            {
              width: "100%",
              height: "100%",
              "@media(max-width:1024px)": {
                boxShadow: "none",
              },
              position: "relative",
            },
          ]}
        >
          {presets?.(options)}
          {calendar?.(options)}
          {time}
        </div>
      )}
    >
      {({ triggerRef, triggerProps }) =>
        children?.({ triggerRef, triggerProps })
      }
    </DynamicPopover>
  );
}

const Presets = ({
  field,
  onSelectionChange,
}: {
  field: Field<RadioGroupState<string>>;
  onSelectionChange?:
    | ((option: RadioGroupOption<string> | null) => void)
    | undefined;
}) => {
  const now = useNow("day");
  const formatDateTime = useFormatDateTime();
  const message = useMessage();
  const { isDesktop } = useScreenDetector();

  const presetOptions = [
    {
      label: `${message({ id: "common.presets.today", default: "Today," })} ${formatDateTime(
        now.toPlainDate(),
        {
          day: "2-digit",
          month: "long",
        },
      )}`,
      value: `${now.toPlainDate()}`,
    },
    {
      label: `${message({ id: "common.presets.tomorrow", default: "Tomorrow," })} ${formatDateTime(
        now.add({ days: 1 }).toPlainDate(),
        {
          day: "2-digit",
          month: "long",
        },
      )}`,
      value: `${now.add({ days: 1 }).toPlainDate()}`,
    },
    {
      label: message({
        id: "common.presets.any-day",
        default: "Any day",
      }),
      value: "",
    },
  ];
  return (
    <div
      css={[
        cssFns.padding(26, 20),
        {
          ...(isDesktop && {
            minWidth: 336,
          }),
        },
        cssFns.borderBlockEnd({
          style: "solid",
          color: colors.principal.grayline,
          width: 1,
        }),
      ]}
    >
      <RadioGroup
        variant="inline-column"
        field={field}
        width={"100%"}
        options={presetOptions}
        onSelectionChange={onSelectionChange}
      />
    </div>
  );
};
